/**
 * Représentation visuelle des statuts d'application et d'états de déploiement.
 *
 * Les libellés d'état de pipeline viennent de `STATE_LABELS` (§3.3) : ils ne
 * sont jamais réécrits ici, pour que le front, l'API et le builder parlent
 * exactement le même langage.
 */

import { STATE_LABELS, type AppStatus, type DeploymentState } from '@portail/shared';

import { Badge, StatusDot, type Tone } from './ui';

/** Libellés des statuts Cloudron d'une application. */
const APP_STATUS_LABELS: Record<AppStatus, string> = {
  running: 'En ligne',
  stopped: 'Arrêtée',
  installing: 'Installation…',
  error: 'En erreur',
  unknown: 'Inconnu',
};

const APP_STATUS_TONES: Record<AppStatus, Tone> = {
  running: 'green',
  stopped: 'slate',
  installing: 'blue',
  error: 'red',
  unknown: 'slate',
};

/** Pastille + libellé du statut d'une application. */
export function AppStatusBadge({ status }: { status: AppStatus }) {
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-sm">
      <StatusDot tone={APP_STATUS_TONES[status]} />
      <span className="text-slate-700 dark:text-slate-300">{APP_STATUS_LABELS[status]}</span>
    </span>
  );
}

/** Teinte associée à un état de déploiement. */
export function deploymentTone(state: DeploymentState): Tone {
  if (state === 'DONE') return 'green';
  if (state === 'FAILED') return 'red';
  if (state === 'CANCELLED') return 'slate';
  if (state === 'QUEUED') return 'amber';
  return 'blue';
}

/**
 * Étiquette d'un état de déploiement. Le code technique est affiché — c'est
 * lui qu'on retrouve dans les logs et les exports — et le libellé complet est
 * fourni en infobulle.
 */
export function DeploymentStateBadge({ state }: { state: DeploymentState }) {
  return (
    <Badge tone={deploymentTone(state)}>
      <span title={STATE_LABELS[state]}>{state}</span>
    </Badge>
  );
}
